/* Anime-Pass — Wishlist
   ---------------------------------------------------------
   Stores a list of { id, handle } objects in localStorage so the
   wishlist page can fetch product data by handle.

   Syncs every [data-ap-fav] button on the page and dispatches
   `ap:wishlist:updated` so other scripts can react.

   Exposes window.AnimePassWishlist for programmatic access. */
(function () {
  'use strict';

  var STORAGE_KEY = 'ap_wishlist';

  /* ---------- Storage helpers ---------- */
  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* quota exceeded — fail silently */
    }
  }

  /* ---------- Public API ---------- */
  function getAll() {
    return read();
  }

  function has(id) {
    id = Number(id);
    return read().some(function (item) { return item.id === id; });
  }

  function add(id, handle) {
    id = Number(id);
    handle = handle || '';
    var list = read();
    if (list.some(function (item) { return item.id === id; })) return list;
    list.push({ id: id, handle: handle });
    write(list);
    notify(list);
    return list;
  }

  function remove(id) {
    id = Number(id);
    var list = read().filter(function (item) { return item.id !== id; });
    write(list);
    notify(list);
    return list;
  }

  function toggle(id, handle) {
    if (has(id)) {
      return remove(id);
    }
    return add(id, handle);
  }

  function count() {
    return read().length;
  }

  function ids() {
    return read().map(function (item) { return item.id; });
  }

  /* ---------- DOM sync ---------- */
  function syncButtons() {
    var favIds = ids();
    var list = read();
    var dirty = false;
    document.querySelectorAll('[data-ap-fav]').forEach(function (btn) {
      var id = Number(btn.getAttribute('data-ap-fav'));
      var active = favIds.indexOf(id) !== -1;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.classList.toggle('is-fav', active);
      if (active) {
        var handle = btn.getAttribute('data-ap-fav-handle');
        if (handle) {
          for (var i = 0; i < list.length; i++) {
            if (list[i].id === id && !list[i].handle) {
              list[i].handle = handle;
              dirty = true;
              break;
            }
          }
        }
      }
    });
    if (dirty) write(list);
  }

  function syncCountBadge() {
    var n = count();
    document.querySelectorAll('.wishlist-count').forEach(function (el) {
      el.textContent = n > 0 ? n : '';
    });
  }

  function notify(list) {
    syncButtons();
    syncCountBadge();
    document.dispatchEvent(new CustomEvent('ap:wishlist:updated', {
      detail: {
        count: list.length,
        ids: list.map(function (item) { return item.id; }),
        items: list,
      },
    }));
  }

  /* ---------- Live announcement ---------- */
  var favStatus = null;

  function ensureStatus() {
    if (favStatus) return favStatus;
    favStatus = document.createElement('div');
    favStatus.className = 'visually-hidden';
    favStatus.setAttribute('aria-live', 'polite');
    favStatus.setAttribute('aria-atomic', 'true');
    favStatus.setAttribute('data-ap-fav-status', '');
    document.body.appendChild(favStatus);
    return favStatus;
  }

  function announceFav(btn, added) {
    var label = btn.getAttribute('aria-label') || '';
    var name = label.replace(/^(Save|Remove)\s+/i, '').trim();
    if (!name) name = 'item';
    var msg = added
      ? 'Added ' + name + ' to your wishlist.'
      : 'Removed ' + name + ' from your wishlist.';
    var el = ensureStatus();
    el.textContent = '';
    requestAnimationFrame(function () { el.textContent = msg; });
  }

  /* ---------- Click handler ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ap-fav]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var id = btn.getAttribute('data-ap-fav');
    var handle = btn.getAttribute('data-ap-fav-handle') || '';
    toggle(id, handle);
    announceFav(btn, has(id));
  });

  /* ---------- Migrate legacy key ---------- */
  function migrateLegacy() {
    var OLD_KEY = 'ap_saved_products';
    try {
      var raw = localStorage.getItem(OLD_KEY);
      if (!raw) return;
      var oldIds = JSON.parse(raw);
      if (!Array.isArray(oldIds) || oldIds.length === 0) {
        localStorage.removeItem(OLD_KEY);
        return;
      }
      var current = read();
      var existing = {};
      current.forEach(function (item) { existing[item.id] = true; });
      oldIds.forEach(function (id) {
        var n = Number(id);
        if (!existing[n]) {
          current.push({ id: n, handle: '' });
          existing[n] = true;
        }
      });
      write(current);
      localStorage.removeItem(OLD_KEY);
    } catch (e) {
      try { localStorage.removeItem(OLD_KEY); } catch (x) {}
    }
  }

  /* ---------- Boot ---------- */
  function boot() {
    migrateLegacy();
    syncButtons();
    syncCountBadge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-sync after Shopify theme editor reloads a section
  document.addEventListener('shopify:section:load', boot);

  // Sync across tabs
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      boot();
      document.dispatchEvent(new CustomEvent('ap:wishlist:updated', {
        detail: {
          count: count(),
          ids: ids(),
          items: getAll(),
        },
      }));
    }
  });

  window.AnimePassWishlist = {
    toggle: toggle,
    has: has,
    getAll: getAll,
    add: add,
    remove: remove,
    count: count,
    ids: ids,
  };
})();
